import { useSavedLocationsApi } from "@/Hooks/useSavedLocationsApi";
import type { UUID } from "@/Shared/types";
import type { AddContactContactType } from "@/Shared/Types/contactTypes";
import type {
  AddDefaultContactRequest,
  AddSavedContactOperationRequest,
  AddSavedLocationOperationRequest,
  DeleteSavedLocationRequest,
  ModifySavedContactOperationRequest,
  ModifySavedLocationOperationRequest,
  SetDefaultContactOperationRequest,
  ToggleBranchRequest,
  ToggleDefaultDeliveryLocationRequest,
  ToggleDefaultPickupLocationRequest,
  ToggleFavouriteRequest,
} from "@freightsimple/generated-dashboard-openapi-client/apis/SavedLocationsApi";
import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";
import type { LoadingCapabilitiesAvailability } from "@freightsimple/generated-dashboard-openapi-client/models/LoadingCapabilitiesAvailability";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import type { SavedContact } from "@freightsimple/generated-dashboard-openapi-client/models/SavedContact";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";

export function useSavedLocationsService() {
  const savedLocationsApi = useSavedLocationsApi();

  return {
    getLocation: async function (
      savedLocationId: UUID,
    ): Promise<SavedLocation> {
      return await savedLocationsApi.getOneSavedLocation({
        savedLocationId,
      });
    },

    addLocation: async function (location: SavedLocation) {
      const request: AddSavedLocationOperationRequest = {
        addSavedLocationRequest: {
          location,
        },
      };

      await savedLocationsApi.addSavedLocation(request);
    },

    modifyLocation: async function (
      locationId: UUID,
      location: Location,
      locationHasDock?: LoadingCapabilitiesAvailability,
      locationHasForklift?: LoadingCapabilitiesAvailability,
    ) {
      const request: ModifySavedLocationOperationRequest = {
        modifySavedLocationRequest: {
          savedLocationId: locationId,
          location: location as Location,
          ...(locationHasDock !== undefined ? { locationHasDock } : {}),
          ...(locationHasForklift !== undefined ? { locationHasForklift } : {}),
        },
      };

      await savedLocationsApi.modifySavedLocation(request);
    },

    toggleIsFavourite: async function (savedLocationId: UUID) {
      const request: ToggleFavouriteRequest = {
        savedLocationId,
      };
      await savedLocationsApi.toggleFavourite(request);
    },

    toggleIsBranch: async function (savedLocationId: UUID) {
      const request: ToggleBranchRequest = {
        savedLocationId,
      };
      await savedLocationsApi.toggleBranch(request);
    },

    toggleDefaultPickupLocation: async function (savedLocationId: UUID) {
      const request: ToggleDefaultPickupLocationRequest = {
        savedLocationId,
      };
      await savedLocationsApi.toggleDefaultPickupLocation(request);
    },

    toggleDefaultDeliveryLocation: async function (savedLocationId: UUID) {
      const request: ToggleDefaultDeliveryLocationRequest = {
        savedLocationId,
      };
      await savedLocationsApi.toggleDefaultDeliveryLocation(request);
    },

    deleteSavedLocation: async function (savedLocationId: UUID) {
      const request: DeleteSavedLocationRequest = {
        deleteAddressBookEntryRequest: {
          addressBookEntryId: savedLocationId,
        },
      };
      await savedLocationsApi.deleteSavedLocation(request);
    },

    addContact: async function (contact: SavedContact) {
      const request: AddSavedContactOperationRequest = {
        addSavedContactRequest: contact,
      };

      await savedLocationsApi.addSavedContact(request);
    },

    deleteContact: async function (savedContactId: UUID) {
      await savedLocationsApi.deleteSavedContact({
        savedContactId,
      });
    },

    addDefaultContact: async function (contact: SavedContact) {
      const request: AddDefaultContactRequest = {
        addSavedContactRequest: contact,
      };

      await savedLocationsApi.addDefaultContact(request);
    },

    setDefaultContact: async function (
      savedLocationId: UUID,
      savedContactId: UUID,
    ) {
      const request: SetDefaultContactOperationRequest = {
        setDefaultContactRequest: {
          savedLocationId,
          savedContactId,
        },
      };

      await savedLocationsApi.setDefaultContact(request);
    },

    modifyContact: async function (
      savedContactId: UUID,
      contact: AddContactContactType,
    ) {
      const request: ModifySavedContactOperationRequest = {
        modifySavedContactRequest: {
          savedContactId,
          // TODO: Assert
          contact: contact as Contact,
        },
      };
      await savedLocationsApi.modifySavedContact(request);
    },
  };
}
